import { type FC, useMemo } from "react";
import { useFormContext } from "react-hook-form";

import { Join } from "../join";
import { Tooltip, TooltipContent, TooltipTrigger } from "../tooltip";
import { useContacts } from "./email-draft-contacts-provider";
import { useEmailDraft } from "./email-draft-context";
import { isRecipientEmailHidden, type ResolvedRecipient, resolveRecipient } from "./email-draft-recipients";
import { EmailDraftRecipientsSkeleton } from "./email-draft-recipients-skeleton";

export const EmailDraftRecipientsDisplay: FC = () => {
  const { showBcc } = useEmailDraft();
  const { watch } = useFormContext();
  const { contacts, isLoading } = useContacts();

  const [to, cc, bcc] = watch(["to", "cc", "bcc"]);

  const recipientsTo = useMemo(
    () => to.map((recipient: string) => resolveRecipient(recipient, contacts)),
    [contacts, to]
  );
  const recipientsCc = useMemo(
    () => cc.map((recipient: string) => resolveRecipient(recipient, contacts)),
    [contacts, cc]
  );
  const recipientsBcc = useMemo(
    () => (showBcc ? bcc.map((recipient: string) => resolveRecipient(recipient, contacts)) : []),
    [contacts, bcc, showBcc]
  );

  if (isLoading) {
    return <EmailDraftRecipientsSkeleton className="h-6" />;
  }

  if (recipientsTo.length === 0 && recipientsCc.length === 0 && recipientsBcc.length === 0) {
    return <div className="text-muted-foreground text-sm cursor-text">Recipients</div>;
  }

  return (
    <div className="cursor-text">
      <Join delimiter={<span className="text-sm text-muted-foreground">; </span>}>
        {recipientsTo.length > 0 && <EmailDraftRecipientsDisplayGroup label="To" recipients={recipientsTo} />}
        {recipientsCc.length > 0 && <EmailDraftRecipientsDisplayGroup label="Cc" recipients={recipientsCc} />}
        {recipientsBcc.length > 0 && <EmailDraftRecipientsDisplayGroup label="Bcc" recipients={recipientsBcc} />}
      </Join>
    </div>
  );
};

const EmailDraftRecipientsDisplayGroup: FC<{ label: string; recipients: ResolvedRecipient[] }> = ({
  label,
  recipients,
}) => (
  <>
    <span className="text-sm text-muted-foreground">{label}:</span>&nbsp;
    <Join delimiter={<span className="text-sm text-muted-foreground">, </span>}>
      {recipients.map((recipient: ResolvedRecipient, index: number) => (
        <EmailDraftRecipientDisplayName recipient={recipient} key={index} />
      ))}
    </Join>
  </>
);

const EmailDraftRecipientDisplayName: FC<{ recipient: ResolvedRecipient }> = ({ recipient }) => {
  const name = <span className="text-sm text-foreground text-nowrap">{recipient.label}</span>;

  if (!isRecipientEmailHidden(recipient)) {
    return name;
  }

  // The tooltip trigger stays on the recipient itself, never on the wrapping element, which doubles
  // as the collapsible trigger for the recipients editor.
  return (
    <Tooltip>
      <TooltipTrigger asChild>{name}</TooltipTrigger>
      <TooltipContent>{recipient.email}</TooltipContent>
    </Tooltip>
  );
};
